import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, DataTable, StatusPill, Loading, Section } from "../components/Common";
import { Search } from "lucide-react";

export default function Students() {
  const { school } = useParams();
  const [students, setStudents] = useState(null);
  const [total, setTotal] = useState(0);
  const [classes, setClasses] = useState([]);
  const [q, setQ] = useState("");
  const [year, setYear] = useState("");

  useEffect(() => {
    api.classes().then(({ data }) => setClasses(data));
    api.students().then((res) => {
      setStudents(res.data);
      setTotal(res.total ?? res.data.length);
    });
  }, []);

  const filtered = useMemo(() => {
    if (!students) return [];
    return students.filter((s) => {
      if (year && s.year_group !== year) return false;
      if (q) {
        const t = `${s.first_name} ${s.last_name} ${s.admission_no}`.toLowerCase();
        if (!t.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [students, q, year]);

  if (students === null) return <Loading />;

  const years = [...new Set((classes || []).map((c) => c.year_group))].sort();

  return (
    <div data-testid="students-page">
      <PageHeader
        kicker="Student Information System"
        title="Students"
        subtitle={`${total} enrolled — search across ${years.length} year groups`}
      />

      <Section>
        <div className="flex gap-3 mb-4">
          <div className="relative flex-1 max-w-md">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name or admission number"
              className="w-full pl-9 pr-4 py-2 text-sm bg-white border border-slate-200 rounded-md"
              data-testid="students-search"
            />
          </div>
          <select
            value={year}
            onChange={(e) => setYear(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-md text-sm bg-white"
            data-testid="students-year-filter"
          >
            <option value="">All year groups</option>
            {years.map((y) => <option key={y}>{y}</option>)}
          </select>
        </div>

        <DataTable
          testId="students-table"
          columns={[
            {
              key: "name", label: "Student",
              render: (r) => (
                <Link to={`/education360/${school}/students/${r.id}`} className="flex items-center gap-3 hover:underline">
                  <div className="w-8 h-8 rounded-full bg-[color:var(--hl-navy)] text-white text-xs grid place-items-center font-semibold">
                    {r.first_name[0]}{r.last_name[0]}
                  </div>
                  <div>
                    <div className="font-semibold text-slate-800">{r.first_name} {r.last_name}</div>
                    <div className="text-xs text-slate-500 mono">{r.admission_no}</div>
                  </div>
                </Link>
              ),
            },
            { key: "year_group", label: "Year" },
            { key: "house", label: "House" },
            { key: "nationality", label: "Nationality" },
            { key: "boarding", label: "Boarding", render: (r) => r.boarding ? <StatusPill status="active" /> : <span className="text-xs text-slate-400">Day</span> },
            { key: "status", label: "Status", render: (r) => <StatusPill status={r.status} /> },
          ]}
          rows={filtered.slice(0, 500)}
        />
      </Section>
    </div>
  );
}
